import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, EnumDecoder, MapDecoder, StringDecoder, field } from '@simonbackx/simple-encoding';
import { Formatter } from '@stamhoofd/utility';
import { v4 as uuidv4 } from 'uuid';
import { AccessRight, AccessRightHelper } from './AccessRight.js';
import { PermissionLevel, getPermissionLevelNumber } from './PermissionLevel.js';
import { PermissionsResourceType } from './PermissionsResourceType.js';
import { ResourcePermissions } from './ResourcePermissions.js';
export class PermissionRole extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.name = '';
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], PermissionRole.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], PermissionRole.prototype, "name", void 0);
export class PermissionRoleDetailed extends PermissionRole {
    constructor() {
        super(...arguments);
        /**
         * Generic access to all resources
         */
        this.level = PermissionLevel.None;
        this.accessRights = [];
        this.resources = new Map();
        /**
         * @deprecated
         * Required for migration
         */
        this.legacyManagePayments = false;
        /**
         * @deprecated
         * Required for migration
         */
        this.legacyFinanceDirector = false;
        /**
         * @deprecated
         * Required for migration
         */
        this.legacyCreateWebshops = false;
    }
    compress() {
        if (this.level === PermissionLevel.Full) {
            this.accessRights = [];
            this.resources = new Map();
        }
    }
    getDiffValue() {
        return this.getDescription();
    }
    getDescription() {
        const stack = [];
        if (this.level === PermissionLevel.Read) {
            stack.push($t(`%m7`));
        }
        if (this.level === PermissionLevel.Write) {
            stack.push($t(`%m8`));
        }
        if (this.level === PermissionLevel.Full) {
            stack.push($t(`%m9`));
            return Formatter.capitalizeFirstLetter(Formatter.joinLast(stack, ', ', ' ' + $t(`%M1`) + ' '));
        }
        for (const right of this.accessRights) {
            stack.push(AccessRightHelper.getDescription(right));
        }
        stack.push(...ResourcePermissions.getMapDescription(this.resources));
        if (stack.length === 0) {
            return $t(`%8v`);
        }
        return Formatter.capitalizeFirstLetter(Formatter.joinLast(stack, ', ', ' ' + $t(`%M1`) + ' '));
    }
    hasAccess(level) {
        return getPermissionLevelNumber(this.level) >= getPermissionLevelNumber(level);
    }
    hasAccessRight(right) {
        const gl = AccessRightHelper.autoGrantRightForLevel(right);
        if ((gl && this.hasAccess(gl)) || this.accessRights.includes(right)) {
            return true;
        }
        const autoInherit = AccessRightHelper.autoInheritFrom(right);
        for (const r of autoInherit) {
            if (this.hasAccessRight(r)) {
                return true;
            }
        }
        return false;
    }
    getResourcePermissions(type, id) {
        const resource = this.resources.get(type);
        if (!resource) {
            return null;
        }
        const rInstance = resource.get(id);
        const allInstance = resource.get('');
        if (!rInstance) {
            if (allInstance) {
                return allInstance;
            }
            return null;
        }
        if (allInstance) {
            return rInstance.merge(allInstance);
        }
        return rInstance;
    }
    getMergedResourcePermissions(type, id) {
        var _a;
        let base = this.getResourcePermissions(type, id);
        if (getPermissionLevelNumber(this.level) > getPermissionLevelNumber((_a = base === null || base === void 0 ? void 0 : base.level) !== null && _a !== void 0 ? _a : PermissionLevel.None)) {
            if (!base) {
                base = ResourcePermissions.create({ level: this.level });
            }
            base.level = this.level;
        }
        return base;
    }
    hasResourceAccess(type, id, level) {
        var _a, _b;
        if (this.hasAccess(level)) {
            return true;
        }
        return (_b = (_a = this.getResourcePermissions(type, id)) === null || _a === void 0 ? void 0 : _a.hasAccess(level)) !== null && _b !== void 0 ? _b : false;
    }
    hasResourceAccessRight(type, id, right) {
        var _a, _b;
        if (this.hasAccessRight(right)) {
            return true;
        }
        return (_b = (_a = this.getResourcePermissions(type, id)) === null || _a === void 0 ? void 0 : _a.hasAccessRight(right)) !== null && _b !== void 0 ? _b : false;
    }
    hasAccessRightForSomeResourceOfType(type, right) {
        if (this.hasAccessRight(right)) {
            return true;
        }
        const resource = this.resources.get(type);
        if (!resource) {
            return false;
        }
        for (const r of resource.values()) {
            if (r.hasAccessRight(right)) {
                return true;
            }
        }
        return false;
    }
    add(other) {
        if (getPermissionLevelNumber(this.level) < getPermissionLevelNumber(other.level)) {
            this.level = other.level;
        }
        for (const right of other.accessRights) {
            if (!this.accessRights.includes(right)) {
                this.accessRights.push(right);
            }
        }
        for (const [type, r] of other.resources) {
            for (const [id, resource] of r) {
                if (!this.resources.has(type)) {
                    this.resources.set(type, new Map());
                }
                const current = this.resources.get(type).get(id);
                if (!current) {
                    this.resources.get(type).set(id, resource);
                }
                else {
                    this.resources.get(type).set(id, current.merge(resource));
                }
            }
        }
    }
    get isEmpty() {
        let resourcesEmpty = true;
        for (const resource of this.resources.values()) {
            for (const r of resource.values()) {
                if (!r.isEmpty) {
                    resourcesEmpty = false;
                    break;
                }
            }
        }
        return this.level === PermissionLevel.None && this.accessRights.length === 0 && resourcesEmpty;
    }
    removeAccessRights(rights) {
        this.accessRights = this.accessRights.filter(r => !rights.includes(r));
        for (const resource of this.resources.values()) {
            for (const r of resource.values()) {
                r.removeAccessRights(rights);
            }
        }
    }
}
__decorate([
    field({ decoder: new EnumDecoder(PermissionLevel), version: 201 })
], PermissionRoleDetailed.prototype, "level", void 0);
__decorate([
    field({
        decoder: new ArrayDecoder(new EnumDecoder(AccessRight)),
        version: 246,
        upgrade: function () {
            const base = [];
            if (this.legacyManagePayments) {
                base.push(AccessRight.OrganizationManagePayments);
            }
            if (this.legacyFinanceDirector) {
                base.push(AccessRight.OrganizationFinanceDirector);
            }
            if (this.legacyCreateWebshops) {
                base.push(AccessRight.OrganizationCreateWebshops);
            }
            return base;
        },
    })
], PermissionRoleDetailed.prototype, "accessRights", void 0);
__decorate([
    field({
        decoder: new MapDecoder(new EnumDecoder(PermissionsResourceType), new MapDecoder(
        // ID
        StringDecoder, ResourcePermissions)),
        version: 248,
    })
], PermissionRoleDetailed.prototype, "resources", void 0);
__decorate([
    field({ decoder: BooleanDecoder, field: 'managePayments', optional: true })
], PermissionRoleDetailed.prototype, "legacyManagePayments", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 199, field: 'financeDirector', optional: true })
], PermissionRoleDetailed.prototype, "legacyFinanceDirector", void 0);
__decorate([
    field({ decoder: BooleanDecoder, field: 'createWebshops', optional: true })
], PermissionRoleDetailed.prototype, "legacyCreateWebshops", void 0);
export class PermissionRoleForResponsibility extends PermissionRoleDetailed {
    constructor() {
        super(...arguments);
        this.responsibilityGroupId = null;
    }
}
__decorate([
    field({ decoder: StringDecoder })
], PermissionRoleForResponsibility.prototype, "responsibilityId", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], PermissionRoleForResponsibility.prototype, "responsibilityGroupId", void 0);
//# sourceMappingURL=PermissionRole.js.map